import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Phone,
  Mail,
  Building2,
  Globe,
  Edit,
  Briefcase,
  Target,
  Users,
  Lightbulb
} from "lucide-react";
import TrainingTracker from "../components/TrainingTracker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MemberDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, chapter } = useAuth();
  const [member, setMember] = useState(null);
  const [powerTeams, setPowerTeams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const token = localStorage.getItem("token");
  const primaryColor = chapter?.primary_color || "#CC0000";
  const canEdit = user?.role === "admin" || member?.email === user?.email;

  const [profileForm, setProfileForm] = useState({
    elevator_pitch: "",
    services_offered: "",
    ideal_client: "",
    current_ask: "",
    target_connections: "",
    referral_triggers: "",
    website: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [memberRes, ptRes, catRes] = await Promise.all([
        fetch(`${API}/members/${id}`, { headers }),
        fetch(`${API}/power-teams`, { headers }),
        fetch(`${API}/categories`, { headers })
      ]);

      if (memberRes.ok) {
        const memberData = await memberRes.json();
        setMember(memberData);
        if (memberData.profile) {
          setProfileForm({
            elevator_pitch: memberData.profile.elevator_pitch || "",
            services_offered: memberData.profile.services_offered || "",
            ideal_client: memberData.profile.ideal_client || "",
            current_ask: memberData.profile.current_ask || "",
            target_connections: memberData.profile.target_connections || "",
            referral_triggers: memberData.profile.referral_triggers || "",
            website: memberData.profile.website || "",
            notes: memberData.profile.notes || ""
          });
        }
      }
      if (ptRes.ok) setPowerTeams(await ptRes.json());
      if (catRes.ok) setCategories(await catRes.json());
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/members/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ profile: profileForm })
      });

      if (response.ok) {
        toast.success("Profile updated");
        setEditDialogOpen(false);
        fetchData();
      } else {
        toast.error("Failed to update profile");
      }
    } catch (error) {
      toast.error("Error updating profile");
    }
  };

  const getPowerTeamName = (ptId) => powerTeams.find(pt => pt.power_team_id === ptId)?.name || "-";
  const getCategoryName = (catId) => categories.find(c => c.category_id === catId)?.name || "-";

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!member) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Member not found</p>
          <Button variant="link" onClick={() => navigate("/members")}>Go back to directory</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="member-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/members")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-4">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl"
                style={{ backgroundColor: primaryColor }}
              >
                {member.full_name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <h1 className="text-3xl font-display font-bold text-slate-900">{member.full_name}</h1>
                <p className="text-muted-foreground">{member.company || "No company"}</p>
              </div>
            </div>
          </div>
          
          {canEdit && (
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Profile
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Profile</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdateProfile} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Elevator Pitch</Label>
                    <Textarea
                      value={profileForm.elevator_pitch}
                      onChange={(e) => setProfileForm({ ...profileForm, elevator_pitch: e.target.value })}
                      placeholder="Your 30-second pitch..."
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Services Offered</Label>
                    <Textarea
                      value={profileForm.services_offered}
                      onChange={(e) => setProfileForm({ ...profileForm, services_offered: e.target.value })}
                      placeholder="What services do you provide?"
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ideal Client</Label>
                    <Textarea
                      value={profileForm.ideal_client}
                      onChange={(e) => setProfileForm({ ...profileForm, ideal_client: e.target.value })}
                      placeholder="Describe your ideal client..."
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Current Ask</Label>
                    <Textarea
                      value={profileForm.current_ask}
                      onChange={(e) => setProfileForm({ ...profileForm, current_ask: e.target.value })}
                      placeholder="What are you looking for right now?"
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Target Connections</Label>
                    <Textarea
                      value={profileForm.target_connections}
                      onChange={(e) => setProfileForm({ ...profileForm, target_connections: e.target.value })}
                      placeholder="Who do you want to be connected to?"
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Referral Triggers</Label>
                    <Textarea
                      value={profileForm.referral_triggers}
                      onChange={(e) => setProfileForm({ ...profileForm, referral_triggers: e.target.value })}
                      placeholder="What signals indicate a referral opportunity?"
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Website</Label>
                    <Input
                      value={profileForm.website}
                      onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={profileForm.notes}
                      onChange={(e) => setProfileForm({ ...profileForm, notes: e.target.value })}
                      placeholder="Additional notes..."
                      rows={2}
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" style={{ backgroundColor: primaryColor }}>Save Profile</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact & Business Info */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-display">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Phone className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Mobile</p>
                      <p className="font-medium">{member.mobile || "-"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Mail className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium">{member.email || "-"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Building2 className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Power Team</p>
                      <p className="font-medium">{getPowerTeamName(member.power_team_id)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Briefcase className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Category</p>
                      <p className="font-medium">{getCategoryName(member.category_id)}</p>
                    </div>
                  </div>
                </div>

                {member.profile?.website && (
                  <>
                    <Separator />
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-slate-100 rounded-lg">
                        <Globe className="h-4 w-4 text-slate-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Website</p>
                        <a 
                          href={member.profile.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="font-medium text-blue-600 hover:underline"
                        >
                          {member.profile.website}
                        </a>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Elevator Pitch */}
            {member.profile?.elevator_pitch && (
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-display">Elevator Pitch</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 leading-relaxed">{member.profile.elevator_pitch}</p>
                </CardContent>
              </Card>
            )}

            {/* Services */}
            {member.profile?.services_offered && (
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-display flex items-center gap-2">
                    <Briefcase className="h-5 w-5" style={{ color: primaryColor }} />
                    Services Offered
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 leading-relaxed">{member.profile.services_offered}</p>
                </CardContent>
              </Card>
            )}

            {/* Ideal Client & Target */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {member.profile?.ideal_client && (
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-display flex items-center gap-2">
                      <Target className="h-5 w-5" style={{ color: primaryColor }} />
                      Ideal Client
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700">{member.profile.ideal_client}</p>
                  </CardContent>
                </Card>
              )}

              {member.profile?.target_connections && (
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-display flex items-center gap-2">
                      <Users className="h-5 w-5" style={{ color: primaryColor }} />
                      Target Connections
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700">{member.profile.target_connections}</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Referral Triggers */}
            {member.profile?.referral_triggers && (
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-display flex items-center gap-2">
                    <Lightbulb className="h-5 w-5" style={{ color: primaryColor }} />
                    Referral Triggers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700">{member.profile.referral_triggers}</p>
                </CardContent>
              </Card>
            )}

            {/* Training Tracker */}
            <TrainingTracker
              memberId={id}
              canEdit={user?.role === "admin" || user?.role === "host"}
              primaryColor={primaryColor}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Current Ask */}
            <Card className="border-0 shadow-sm" style={{ borderTop: `4px solid ${primaryColor}` }}>
              <CardHeader>
                <CardTitle className="text-lg font-display">Current Ask</CardTitle>
              </CardHeader>
              <CardContent>
                {member.profile?.current_ask ? (
                  <p className="text-slate-700">{member.profile.current_ask}</p>
                ) : (
                  <p className="text-muted-foreground italic">No current ask set</p>
                )}
              </CardContent>
            </Card>

            {/* Member Info */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-display">Member Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Role</span>
                  <Badge variant="outline" className="capitalize">{member.role}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Status</span>
                  <Badge className={member.status === "active" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"}>
                    {member.status}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Join Date</span>
                  <span className="font-medium">{member.join_date || "-"}</span>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            {member.profile?.notes && (
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-display">Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 text-sm">{member.profile.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MemberDetailPage;
